import { useSuspenseQuery } from '@tanstack/react-query';
import c from 'classnames';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import {
  getConditionMetricsListQuery,
  type ConditionWithActionItems,
} from 'services/conditions/useConditionMetrics';

import { KitType } from '@repo/types';

import ConditionMetricCard from './ConditionMetricCard';

import conditionsAdult from 'assets/images/conditions-adult.png';
import conditionsBaby from 'assets/images/conditions-baby.png';
import conditionsChild from 'assets/images/conditions-child.png';
import conditionsToddler from 'assets/images/conditions-toddler.png';
import conditionsVaginal from 'assets/images/conditions-vaginal.png';

import { getHasModalOpened } from 'store/modal';

import {
  Banner,
  Button,
  ContainerSizes,
  getSvgByName,
  Icon,
  Modal,
  PageContainer,
  TextColor,
  Typography,
} from '@repo/ui';
import {
  ButtonColors,
  ButtonSizes,
  Card,
  LinkButton,
  TextLink,
} from 'components';

import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import { useLocation, useParams } from 'react-router-dom';
import useImpersonate from 'services/auth/useImpersonate';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import {
  addNonDiagnosticTinyAccountId,
  getAgreementByTinyAccountId,
} from 'store/agreements';
import { getKitById } from 'store/kits';
import styles from './ConditionMetricsList.module.css';

export const ConditionsInfoModal = () => {
  const [isChecked, setIsChecked] = useState(false);
  const { kitId } = useParams<{ kitId: string }>();
  const kit = useSelector(getKitById(kitId));
  const tinyAccountId = kit?.tinyaccount_id;
  const hasNonConditionsAgreement = useSelector(
    getAgreementByTinyAccountId('nonDiagnostic', tinyAccountId ?? ''),
  );
  const dispatch = useDispatch();
  const { impersonateId } = useImpersonate();

  async function handleUpdateTinyAccountAcknowledgement() {
    if (!tinyAccountId) return;

    dispatch(addNonDiagnosticTinyAccountId(tinyAccountId));
  }

  return (
    <Modal
      isOpen={!hasNonConditionsAgreement && !impersonateId}
      isDismissable={false}
      className={styles.modal}
    >
      <div className='flex flex-column align-items-center gap-3 text-center'>
        <div className='flex flex-column align-items-center gap-2'>
          <div className={styles.circledIcon}>
            <Icon name='alertTriangleFilled' size='l' />
          </div>
          <Typography
            variant='heading-s'
            className={c('mb-2 font-bold')}
            responsive
          >
            This test does not diagnose any health conditions
          </Typography>
        </div>

        <div className='flex flex-column gap-4'>
          <Typography variant='body-xs' className={styles.secondaryText}>
            This section shows metrics that may be associated with these
            conditions. Lifestyle, genetics, and other factors may also
            influence the presence of these conditions.
          </Typography>
          <div
            className={styles.checkLine}
            onClick={() => setIsChecked(!isChecked)}
          >
            <input type='checkbox' checked={isChecked} readOnly />
            <Typography variant='body-s' responsive>
              I understand this test does not diagnose conditions.
            </Typography>
          </div>

          <Button
            color='blue'
            width={'full'}
            disabled={!isChecked}
            onClick={handleUpdateTinyAccountAcknowledgement}
          >
            Proceed
          </Button>
        </div>
      </div>
    </Modal>
  );
};
export const ConditionMetricsList = () => {
  const dispatch = useDispatch();
  const { currentKit, currentKitId } = useCurrentKitAndTinyAccount();
  const { pathname } = useLocation();

  const { data: conditionData } = useSuspenseQuery<ConditionWithActionItems[]>(
    getConditionMetricsListQuery(currentKitId),
  );

  const [isOpen, setIsOpen] = useState(false);

  const conditionsDisclaimerModal = {
    name: 'conditionsDisclaimer',
    props: { kitId: currentKitId },
  };
  const hasOpenedConditionsTable = useSelector(
    getHasModalOpened(conditionsDisclaimerModal),
  );
  useEffect(() => {
    if (!hasOpenedConditionsTable) {
      setIsOpen(true);
    }
  }, [hasOpenedConditionsTable]);

  const { trackEvent } = useAnalytics();
  useEffect(() => {
    if (!currentKitId) return;
    trackEvent('Conditions Page Viewed');
  }, [currentKitId, trackEvent]);

  if (!currentKitId || !currentKit) return <></>;

  const { sampling_type, age_at_sampling } = currentKit;

  let type;
  let imgType;
  if (sampling_type === KitType.VAGINAL) {
    type = 'Vaginal';
    imgType = conditionsVaginal;
  } else if (age_at_sampling! < 365) {
    type = 'Baby';
    imgType = conditionsBaby;
  } else if (age_at_sampling! < 1095) {
    type = 'Toddler';
    imgType = conditionsToddler;
  } else if (age_at_sampling! < 6570) {
    type = 'Child';
    imgType = conditionsChild;
  } else {
    type = 'Adult';
    imgType = conditionsAdult;
  }

  return (
    <PageContainer size={ContainerSizes.LG} className={styles.pageContainer}>
      <ConditionsInfoModal />
      <div className='flex flex-column gap-4'>
        <Breadcrumb className={styles.breadcrumb} />
        <Banner
          color='purple'
          title='This test does not diagnose any health conditions.'
          content='These metrics show microbiome associations with health conditions, not causes. Multiple factors including genetics, diet, and lifestyle contribute to risk. This is a wellness test—not a clinical diagnostic—and does not diagnose any condition. Consult your healthcare provider before making health decisions based on this information.'
          icon={getSvgByName('alertTriangleFilled')}
          variant='full'
          size='lg'
          border
        />
        <Card variant='wrapper' className={styles.cardContainer}>
          <div className={styles.card}>
            <div>
              <div className='flex justify-between align-items-center mb-3'>
                <Typography variant={'heading-s'} className='mb-2'>
                  Metrics related to conditions
                </Typography>
              </div>
              <div className='flex flex-column gap-3 mb-4'>
                <Typography variant={'heading-l'} className={styles.title}>
                  {`${type} conditions & the microbiome`}
                </Typography>
              </div>
            </div>
          </div>
          <div className={styles.conditionMetricsGridListContainer}>
            {conditionData?.map(condition => (
              <ConditionMetricCard
                key={condition.condition_id}
                title={condition.condition_display_name}
                action_items={condition.action_items_count}
                link={`${condition.condition}`}
                evaluationCounts={{
                  great: condition.good,
                  okay: condition.okay,
                  improve: condition.improve,
                  support: condition.support,
                }}
              />
            ))}
          </div>
        </Card>

        <Card variant='wrapper' className={styles.cardContainer}>
          <div>
            <div className='flex justify-between align-items-center mb-3'>
              <Typography variant={'heading-s'}>Emerging Research</Typography>
            </div>
            <div className={styles.researchCardContent}>
              <Typography variant={'heading-s'} className='dark-grey'>
                View the prior emerging research on biomarkers and associations.
              </Typography>
              <TextLink
                label='Read more'
                iconNameRight='chevronForward'
                to={`/results/${currentKitId}/conditions/emerging-research`}
                state={{ from: pathname }}
                color={TextColor.PURPLE}
                className={styles.cardLink}
              />
            </div>
          </div>
        </Card>

        <div className={styles.actionPlanButton}>
          <LinkButton
            href={`/action-plan/${currentKitId}`}
            size={ButtonSizes.M}
            color={ButtonColors.PURPLE}
            label='Go to action plan'
            width={'full'}
          />
        </div>
      </div>
    </PageContainer>
  );
};
